import { Accordion, Flex, Group, Stack, Title, Tooltip } from "@mantine/core";
import { useConfiguratorComponents } from "../../../hooks/useConfiguratorComponents";
import type {
  Connection,
  Consumer,
  Generator,
  Person,
  Storage
} from "../../../utils/backend-types";
import { ObjectName, type SiteName } from "../../../utils/enums";
import { getCompanyFeatures } from "../../ComponentListContainer/ComponentList/ComponentEditWizard/Data/utils/getCompanyFeatures";
import { ComponentOpenEditModalLink } from "../../ComponentListContainer/ComponentOpenEditModalLink/ComponentOpenEditModalLink";
import { IconHelpText } from "../../IconHelpText/IconHelpText";
import "./PersonsWidget.scss";

declare const SITE_NAME: SiteName;
interface PersonsWidgetProps {
  connectingPartyIds: Array<number>;
  persons: Array<Person>;
  relatedPersonIds: Array<number>;
  projectId: string;
  siteId: number;
}

function PersonsWidget({
  connectingPartyIds,
  persons,
  relatedPersonIds,
  projectId,
  siteId
}: PersonsWidgetProps) {
  const PERSONS_WIDGET_HELP_TEXT = `Schwarzer Rand kennzeichnet Unternehmen mit aktiven Add-ons in ${SITE_NAME}.`;

  const Body = (
    <div className="persons-row">
      {persons.map((person) => (
        <PersonIcon
          isOnlyConnectingParty={
            !relatedPersonIds.includes(person.id) &&
            connectingPartyIds.includes(person.id)
          }
          key={person.id}
          person={person}
          projectId={projectId}
          siteId={siteId}
        />
      ))}
    </div>
  );

  return (
    <div className="PersonsWidget">
      {persons.length > 5 ? (
        <Accordion>
          <Accordion.Item value="persons">
            <Accordion.Control>
              <Group gap="xs">
                <Title order={5}>Unternehmen ({persons.length})</Title>
                <IconHelpText helpText={PERSONS_WIDGET_HELP_TEXT} />
              </Group>
            </Accordion.Control>
            <Accordion.Panel>{Body}</Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      ) : (
        <>
          <Group gap="xs" mb="sm">
            <Title order={5}>Unternehmen</Title>
            <IconHelpText helpText={PERSONS_WIDGET_HELP_TEXT} />
          </Group>
          {Body}
        </>
      )}
    </div>
  );
}

interface PersonsWidgetDataWrapperProps {
  siteId: number;
  variantId: number;
  projectId: string;
}

function PersonsWidgetDataWrapper({
  siteId,
  variantId,
  projectId
}: PersonsWidgetDataWrapperProps) {
  const { data: generators } = useConfiguratorComponents<Generator>(
    ObjectName.Generator,
    siteId
  );
  const { data: consumers } = useConfiguratorComponents<Consumer>(
    ObjectName.Consumer,
    siteId
  );
  const { data: storages } = useConfiguratorComponents<Storage>(
    ObjectName.Storage,
    siteId
  );
  const { data: persons } = useConfiguratorComponents<Person>(
    ObjectName.Person,
    variantId
  );
  const { data: connections } = useConfiguratorComponents<Connection>(
    ObjectName.Connection,
    siteId
  );

  if (
    !persons ||
    persons.length === 0 ||
    !generators ||
    !consumers ||
    !storages ||
    !connections
  ) {
    return null;
  }

  const relatedPersonIds = [
    ...generators.map((generator) => generator.person),
    ...consumers.map((consumer) => consumer.person),
    ...storages.map((storage) => storage.person)
  ];

  const connectingPartyIds = connections
    .map((connection) => connection.connectingParty)
    .filter((connectingParty) => connectingParty !== null);

  const filteredPersons = persons.filter(
    (person) =>
      relatedPersonIds.includes(person.id) ||
      connectingPartyIds.includes(person.id)
  );

  return (
    <PersonsWidget
      connectingPartyIds={connectingPartyIds}
      persons={filteredPersons}
      projectId={projectId}
      relatedPersonIds={relatedPersonIds}
      siteId={siteId}
    />
  );
}

interface PersonIconProps {
  person: Person;
  isOnlyConnectingParty: boolean;
  projectId: string;
  siteId: number;
}

function PersonIcon({
  person,
  isOnlyConnectingParty,
  projectId,
  siteId
}: PersonIconProps) {
  const name = isOnlyConnectingParty
    ? `${person.name} (Netzanschlussnehmer)`
    : person.name;

  const companyFeatures = getCompanyFeatures(person);
  const hasCompanyFeature = companyFeatures.length > 0;
  return (
    <Stack className="PersonIcon" gap="xs" mr="xs">
      <Flex align="center" gap="xs">
        <div
          className="person-color-box"
          style={{
            backgroundColor: `#${person.color}`,
            border: hasCompanyFeature ? "2px solid black" : "none",
            borderRadius: "50%"
          }}
        />
        {companyFeatures.map((feature) => {
          const Icon = feature.icon;
          return (
            <Tooltip key={feature.key} label={feature.label}>
              <Icon />
            </Tooltip>
          );
        })}
      </Flex>
      <div>
        <ComponentOpenEditModalLink
          componentId={person.id}
          linkContent={name}
          objectName={ObjectName.Person}
          projectId={projectId}
          siteId={siteId}
        />
      </div>
    </Stack>
  );
}

export {
  PersonsWidgetDataWrapper as PersonsWidget,
  PersonsWidget as PersonsWidgetComponent,
  PersonsWidgetProps as PersonsWidgetComponentProps,
  PersonsWidgetDataWrapperProps as PersonsWidgetProps
};
